export interface PipelineStage {
  id: string;
  externalId?: string; // CRM-imported stage code (e.g. HubSpot "closedwon") → maps to display name
  name: string;
  /**
   * Set by the backend only while this is still Coffee's own shipped wording;
   * cleared on rename. Resolve it through `stageLabel`/`useStageLabel` for
   * display — `name` remains the identifier.
   */
  i18nKey?: string | null;
  pct: number;
  color: string;
  won?: boolean;  // backend terminal-stage flags; drive ordering when present
  lost?: boolean;
}

// Classification reads the backend `won`/`lost` flags, which are non-null on
// every stage the API returns. It used to fall back to matching English words in
// the stage NAME ("won", "closed", "lost"…), which silently misclassified every
// stage a user named in their own language — a French "Gagné" counted as open,
// so it never showed as won anywhere, and the stage a Dutch user called
// "Gesloten" was not lost either. A stage with no flag at all is open: that is
// what the backend defaults to, and guessing from the label is the bug.
export function isWonPipelineStage(stage: { won?: boolean }): boolean {
  return stage.won === true;
}

export function isLostPipelineStage(stage: { lost?: boolean }): boolean {
  return stage.lost === true;
}

export function isOpenPipelineStage(stage: { won?: boolean; lost?: boolean }): boolean {
  return !isWonPipelineStage(stage) && !isLostPipelineStage(stage);
}

// Terminal (Closed Won / Closed Lost) stages are pinned to the end regardless of their
// percentage — otherwise stages that share a percentage (or a Closed Lost set to 0%) land
// mid-board. Lost stages always come very last, after won: they are the dead end of the
// pipeline, so they belong past the happy path rather than between it and Closed Won.
// Non-terminal stages sort by percentage.
export function sortPipelineStages<T extends { name: string; pct: number; won?: boolean; lost?: boolean }>(stages: T[]): T[] {
  const nonTerminal: T[] = [];
  const lost: T[] = [];
  const won: T[] = [];
  for (const stage of stages) {
    if (isWonPipelineStage(stage)) won.push(stage);
    else if (isLostPipelineStage(stage)) lost.push(stage);
    else nonTerminal.push(stage);
  }
  nonTerminal.sort((a, b) => a.pct - b.pct);
  return [...nonTerminal, ...won, ...lost];
}

export interface Pipeline {
  id: string;
  name: string;
  /**
   * Set while this is still Coffee's own shipped wording; cleared on rename.
   * Resolve through `pipelineLabel`/`usePipelineLabel` for display — `name`
   * remains the identifier.
   */
  i18nKey?: string | null;
  stages: PipelineStage[];
  showSummaryStats?: boolean;
}

// Preview-mode pipelines. The terminal stages carry explicit `won`/`lost` flags
// because that is now the only thing that classifies them — they used to be
// recognised by the English words in their names, which is precisely the
// heuristic that broke for non-English workspaces.
export const defaultPipelines: Pipeline[] = [
  {
    id: "1",
    name: "Sales Pipeline",
    showSummaryStats: true,
    stages: [
      { id: "s1", name: "Qualification", pct: 10, color: "#93c5fd" },
      { id: "s2", name: "Discovery", pct: 25, color: "#c4b5fd" },
      { id: "s3", name: "Proposal", pct: 50, color: "#fcd34d" },
      { id: "s4", name: "Negotiation", pct: 75, color: "#fb923c" },
      { id: "s5", name: "Closed Won", pct: 100, color: "#86efac", won: true },
      { id: "s6", name: "Closed Lost", pct: 0, color: "#fca5a5", lost: true },
    ],
  },
  {
    id: "2",
    name: "Onboarding & Renewal",
    showSummaryStats: true,
    stages: [
      { id: "o1", name: "Kickoff", pct: 10, color: "#93c5fd" },
      { id: "o2", name: "Implementation", pct: 30, color: "#c4b5fd" },
      { id: "o3", name: "Training", pct: 50, color: "#fcd34d" },
      { id: "o4", name: "Go Live", pct: 70, color: "#fb923c" },
      { id: "o5", name: "Adoption", pct: 85, color: "#5eead4" },
      { id: "o6", name: "Renewal", pct: 95, color: "#f0abfc" },
      { id: "o7", name: "Renewed", pct: 100, color: "#86efac", won: true },
      { id: "o8", name: "Churned", pct: 0, color: "#fca5a5", lost: true },
    ],
  },
];

export const DEFAULT_PIPELINE_ID = "1";
